import MailListItemExpansionLoadMoreButton from './MailListItemExpansionLoadMoreButton';
import MailListItemPart from './MailListItemPart';
import { observer } from 'mobx-react';
import { ClientItemId } from 'owa-client-ids';
import Draggable from 'owa-dnd/lib/components/Draggable';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { isFeatureEnabled } from 'owa-feature-flags';
import listViewStore from 'owa-mail-list-store/lib/store/Store';
import isNewestOnBottom from 'owa-mail-reading-pane-store/lib/utils/isNewestOnBottom';
import ConversationItemParts from 'owa-mail-store/lib/store/schema/ConversationItemParts';
import mailStore from 'owa-mail-store/lib/store/Store';
import canConversationLoadMore from 'owa-mail-store/lib/utils/canConversationLoadMore';
import { getItemToShowFromNodeId } from 'owa-mail-store/lib/utils/conversationsUtils';
import { MailListItemPartDragData } from 'owa-mail-types/lib/types/MailListItemPartDragData';
import strings from 'owa-strings/owa-mail-strings';
import * as React from 'react';
import {
    MAIL_ITEM_DRAG_YOFFSET,
    MAIL_ITEM_DRAG_XOFFSET,
    getMailListItemPartDragPreview,
} from '../utils/mailListDragUtil';
import DisplayDensityMode from 'owa-service/lib/contract/DisplayDensityMode';

const styles = require('./MailListItemPart.scss');

export interface MailListItemExpansionProps {
    conversationId: string;
    tableViewId: string;
    isSingleLine: boolean;
    tableViewSupportsFlagging: boolean;
    densityMode: DisplayDensityMode;
}

export default observer(function MailListItemExpansion(props: MailListItemExpansionProps) {
    const conversationItem = listViewStore.conversationItems.get(props.conversationId);
    const displayItemParts = conversationItem.globalItemIds.length > 1;
    const conversationItemParts: ConversationItemParts = mailStore.conversations.get(
        conversationItem.id
    );
    // If collapsed items roll up is enabled, the root node of the conversation is returned.
    const hasRootNode = isFeatureEnabled('rp-collapsedItemsRollUp');
    const isNewestItemsOnBottom = isNewestOnBottom();
    const loadMoreButton =
        canConversationLoadMore(conversationItem.id) &&
        renderLoadMoreButton(
            conversationItem.clientId,
            conversationItemParts.isLoadMoreInProgress,
            isNewestItemsOnBottom,
            props.isSingleLine
        );

    // The position of the load more button has 4 possibe cases:
    // 1. On the top of the list: if this conversation doesn't have root node and it's newest on bottom.
    // 2. Under the first item: if this conversation has root node and it's newest on bottom.
    // 3. Before the last item: if this conversation has root node and it's newest on top.
    // 4. On the bottom of the list: if this conversation doesn't have root node and it's newest on top.

    return displayItemParts && conversationItemParts ? (
        <div className={styles.expansionContainer}>
            {!hasRootNode && isNewestItemsOnBottom && loadMoreButton}
            <Draggable
                canDrag={canDrag}
                getDragData={() => getMailListExpansionDragData(props.tableViewId)}
                getDragPreview={getMailListItemPartDragPreview}
                xOffset={MAIL_ITEM_DRAG_XOFFSET}
                yOffset={MAIL_ITEM_DRAG_YOFFSET}>
                {conversationItemParts.conversationNodeIds.length > 0 &&
                    conversationItemParts.conversationNodeIds.map((nodeId, index) => {
                        const mailListItemPart = (
                            <MailListItemPart
                                key={nodeId}
                                allNodeIds={conversationItemParts.conversationNodeIds}
                                nodeId={nodeId}
                                tableViewId={props.tableViewId}
                                isSingleLine={props.isSingleLine}
                                tableViewSupportsFlagging={props.tableViewSupportsFlagging}
                                densityMode={props.densityMode}
                            />
                        );

                        if (hasRootNode && loadMoreButton) {
                            if (isNewestItemsOnBottom && index == 0) {
                                return (
                                    <>
                                        {mailListItemPart}
                                        {loadMoreButton}
                                    </>
                                );
                            } else if (
                                !isNewestItemsOnBottom &&
                                index == conversationItemParts.conversationNodeIds.length - 1
                            ) {
                                return (
                                    <>
                                        {loadMoreButton}
                                        {mailListItemPart}
                                    </>
                                );
                            }
                        }
                        return mailListItemPart;
                    })}
            </Draggable>
            {!hasRootNode && !isNewestItemsOnBottom && loadMoreButton}
        </div>
    ) : null;
});

const canDrag = () => {
    return listViewStore.expandedConversationViewState.selectedNodeIds.length > 0;
};

const getMailListExpansionDragData = (tableViewId: string): MailListItemPartDragData => {
    // Also rename this to getMailListRowDragData. May be get this information from the mailRowData getter api
    const itemIds = [];
    const subjects = [];
    const selectedNodeIds = listViewStore.expandedConversationViewState.selectedNodeIds;
    selectedNodeIds.forEach(nodeId => {
        const item = getItemToShowFromNodeId(nodeId);
        if (item) {
            subjects.push(item.Subject ? item.Subject : strings.noSubject);
            itemIds.push(item.ItemId.Id);
        }
    });

    return {
        itemType: DraggableItemTypes.MailListItemPart,
        tableViewId: tableViewId,
        subjects: subjects,
        itemIds: itemIds,
        nodeIds: selectedNodeIds,
    };
};

const renderLoadMoreButton = (
    conversationId: ClientItemId,
    isLoadMoreInProgress: boolean,
    isNewestOnBottom: boolean,
    isSingleLine: boolean
): JSX.Element => {
    return (
        <MailListItemExpansionLoadMoreButton
            conversationId={conversationId}
            isLoadMoreInProgress={isLoadMoreInProgress}
            isSingleLineView={isSingleLine}
            isNewestOnBottom={isNewestOnBottom}
        />
    );
};
